/**
 * PublicSite - Renders published websites for public viewing
 * No authentication required - this is the live site visitors see
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Loader2, Mail, Phone, MapPin, ExternalLink,
  Twitter, Instagram, Linkedin, Youtube, Github, Globe
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  github: Github,
  website: Globe
};

export default function PublicSite() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSite();
  }, [slug]);

  const fetchSite = async () => {
    try {
      const response = await axios.get(`${API}/website-builder/public/${slug}`);
      setSite(response.data);
    } catch (err) {
      console.error('Failed to fetch site:', err);
      setError(err.response?.data?.detail || 'Site not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Site Not Found</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  // Render based on site type
  if (site.type === 'bio_page') {
    return <BioPageRenderer site={site} />;
  }

  return <LandingPageRenderer site={site} />;
}

// Landing Page Renderer
function LandingPageRenderer({ site }) {
  const primaryColor = site.primary_color || '#14b8a6';
  const sections = site.sections || [];

  // Debug: log what we received
  console.log('Site data:', site);
  console.log('Sections:', sections);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Show hero fallback if no sections or empty sections */}
      {sections.length === 0 && (
        <section className="py-24 px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {site.business_name || 'Welcome'}
          </h1>
          {site.business_description && (
            <p className="text-xl text-zinc-300 max-w-2xl mx-auto">
              {site.business_description}
            </p>
          )}
        </section>
      )}

      {sections.map((section, index) => (
        <SectionRenderer 
          key={section.id || index} 
          section={section} 
          primaryColor={primaryColor}
          businessName={site.business_name}
        />
      ))}
      
      {/* Simple Footer */}
      <footer className="py-8 text-center border-t border-zinc-800">
        <p className="text-zinc-500 text-sm">
          &copy; {new Date().getFullYear()} {site.business_name}. All rights reserved.
        </p>
        <p className="text-zinc-600 text-xs mt-2">
          Powered by <a href="https://tripletc.com" className="text-teal-500 hover:underline">TripleTC</a>
        </p>
      </footer>
    </div>
  );
}

// Section Renderer
function SectionRenderer({ section, primaryColor, businessName }) {
  const content = section.content || {};
  
  switch (section.type) {
    case 'hero':
      return (
        <section className="relative py-24 px-6 overflow-hidden">
          <div 
            className="absolute inset-0 opacity-20"
            style={{ background: `linear-gradient(135deg, ${primaryColor}40 0%, transparent 50%)` }}
          />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              {content.headline || `Welcome to ${businessName}`}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-zinc-300 mb-8"
            >
              {content.subheadline || 'Your trusted partner'}
            </motion.p>
            {content.cta_text && (
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                href={content.cta_link || '#contact'}
                className="inline-block px-8 py-4 rounded-full text-white font-semibold text-lg transition-transform hover:scale-105"
                style={{ backgroundColor: primaryColor }}
              >
                {content.cta_text}
              </motion.a>
            )}
          </div>
        </section>
      );

    case 'about':
      return (
        <section className="py-20 px-6 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              {content.title || 'About Us'}
            </h2>
            <div className="text-zinc-300 text-lg leading-relaxed whitespace-pre-line">
              {content.content || 'We are dedicated to serving our customers with excellence.'}
            </div>
          </div>
        </section>
      );

    case 'services':
      const services = content.services || [];
      return (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              {content.title || 'Our Services'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, i) => (
                <div 
                  key={i}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors"
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <span className="text-2xl" style={{ color: primaryColor }}>
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {service.name}
                  </h3>
                  <p className="text-zinc-400">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'contact':
      return (
        <section id="contact" className="py-20 px-6 bg-zinc-900/50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              {content.title || 'Get In Touch'}
            </h2>
            <form className="space-y-6">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Your Email"
                  className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none"
                />
              </div>
              <div>
                <textarea
                  placeholder="Your Message"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-lg text-white font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                {content.cta_text || 'Send Message'}
              </button>
            </form>
          </div>
        </section>
      );

    case 'features':
      const features = content.features_list || [];
      return (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              {content.title || 'Features'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <span style={{ color: primaryColor }}>✓</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{feature.name || feature}</h3>
                    {feature.description && (
                      <p className="text-zinc-400 text-sm mt-1">{feature.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'testimonials':
      const testimonials = content.testimonials_list || [];
      return (
        <section className="py-20 px-6 bg-zinc-900/50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              {content.title || 'What Our Clients Say'}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6">
                  <p className="text-zinc-300 italic mb-4">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-700" />
                    <div>
                      <p className="text-white font-semibold">{t.name}</p>
                      <p className="text-zinc-500 text-sm">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="py-20 px-6">
          <div 
            className="max-w-4xl mx-auto rounded-3xl p-12 text-center"
            style={{ backgroundColor: `${primaryColor}15`, border: `1px solid ${primaryColor}30` }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              {content.headline || 'Ready to Get Started?'}
            </h2>
            <p className="text-zinc-300 mb-8">
              {content.subheadline || 'Take the next step today.'}
            </p>
            <a
              href={content.cta_link || '#contact'}
              className="inline-block px-8 py-4 rounded-full text-white font-semibold transition-transform hover:scale-105"
              style={{ backgroundColor: primaryColor }}
            >
              {content.cta_text || 'Contact Us'}
            </a>
          </div>
        </section>
      );

    default:
      // For unknown section types, try to render something useful
      console.log('Unknown section type:', section.type, section);
      if (content.title || content.headline) {
        return (
          <section className="py-20 px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                {content.title || content.headline}
              </h2>
              {(content.content || content.subheadline || content.description) && (
                <p className="text-zinc-300">
                  {content.content || content.subheadline || content.description}
                </p>
              )}
            </div>
          </section>
        );
      }
      return null;
  }
}

// Bio Page Renderer
function BioPageRenderer({ site }) {
  const primaryColor = site.primary_color || '#14b8a6';
  const links = site.links || [];
  const socialLinks = site.social_links || [];

  const trackClick = async (linkIndex) => {
    try {
      await axios.post(`${API}/website-builder/track-click`, {
        site_id: site.id,
        link_index: linkIndex
      });
    } catch (err) {
      // Silent fail for tracking
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-6"
      style={{ 
        background: `linear-gradient(180deg, ${primaryColor}15 0%, #0a0a0a 50%)` 
      }}
    >
      <div className="max-w-md mx-auto">
        {/* Profile */}
        <div className="text-center mb-8">
          {site.profile_image_url ? (
            <img 
              src={site.profile_image_url} 
              alt={site.name}
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4"
              style={{ borderColor: primaryColor }}
            />
          ) : (
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white"
              style={{ backgroundColor: primaryColor }}
            >
              {site.name?.charAt(0) || '?'}
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{site.name}</h1>
          <p className="text-zinc-400">{site.title}</p>
          {site.bio && (
            <p className="text-zinc-300 mt-3 text-sm">{site.bio}</p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3 mb-8">
          {links.map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(i)}
              className="block w-full py-4 px-6 rounded-xl text-center font-medium transition-all hover:scale-[1.02] hover:shadow-lg"
              style={{ 
                backgroundColor: `${primaryColor}20`,
                border: `1px solid ${primaryColor}40`,
                color: 'white'
              }}
            >
              <span className="flex items-center justify-center gap-2">
                {link.label}
                <ExternalLink className="w-4 h-4 opacity-50" />
              </span>
            </a>
          ))}
        </div>

        {/* Social Links */}
        {socialLinks.length > 0 && (
          <div className="flex justify-center gap-4">
            {socialLinks.map((social, i) => {
              const Icon = SOCIAL_ICONS[social.platform?.toLowerCase()] || Globe;
              return (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{ backgroundColor: `${primaryColor}30` }}
                >
                  <Icon className="w-5 h-5" style={{ color: primaryColor }} />
                </a>
              );
            })}
          </div>
        )}

        {/* Powered by */}
        <p className="text-center text-zinc-600 text-xs mt-12">
          Powered by <a href="https://tripletc.com" className="text-teal-500 hover:underline">TripleTC</a>
        </p>
      </div>
    </div>
  );
}
